import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../services/ApiService';
import type { UsageSummary } from '../types/agent';

/** Re-read cadence while the pane is visible: other members' spend, the index classifier's calls and portal
 *  purchases or cap changes reach the bar without a stream `usage` event. */
export const USAGE_POLL_MS = 60_000;
/** Retry delays after a failed read; the last one repeats. The last known value stays on screen meanwhile. */
export const USAGE_RETRY_MS = [5_000, 15_000, 30_000, 60_000] as const;
/** Focus and visibility re-reads closer than this to the previous one are skipped (focus fires on every click in). */
export const USAGE_FOCUS_GAP_MS = 5_000;

function clearRetry(retry: { current: number | null }): void {
  if (retry.current !== null) window.clearTimeout(retry.current);
  retry.current = null;
}

function visible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

/** Usage bar data. Read on mount, when the pane comes back into view (focus, visibilitychange), every
 *  USAGE_POLL_MS while visible, and on `refresh()` (ChatPanel: thread change, turn end); the stream's `usage`
 *  events feed it through `setUsage`. A read answers only if nothing newer landed since it started (a later read or a
 *  stream value), so an older GET never overwrites a newer value. A failed read keeps the last value and retries
 *  with backoff. */
export function useUsage(): { usage: UsageSummary | null; refresh: () => Promise<void>; setUsage: (u: UsageSummary) => void } {
  const [usage, setState] = useState<UsageSummary | null>(null);
  const seq = useRef(0);
  const lastStart = useRef(0);
  const failures = useRef(0);
  const retry = useRef<number | null>(null);
  const alive = useRef(true);

  const refresh = useCallback(async () => {
    const mine = ++seq.current;
    lastStart.current = Date.now();
    clearRetry(retry);
    try {
      const next = await apiService.getUsage();
      failures.current = 0;
      if (alive.current && seq.current === mine) setState(next);
    } catch (err) {
      console.warn('[agent-v2] usage unavailable', err);
      if (!alive.current || seq.current !== mine) return;
      const delay = USAGE_RETRY_MS[Math.min(failures.current, USAGE_RETRY_MS.length - 1)];
      failures.current += 1;
      retry.current = window.setTimeout(() => void refresh(), delay);
    }
  }, []);

  const setUsage = useCallback((u: UsageSummary) => {
    seq.current += 1;          // any read in flight is now older than this value
    failures.current = 0;
    clearRetry(retry);
    setState(u);
  }, []);

  useEffect(() => {
    alive.current = true;
    void refresh();
    const onFocus = () => {
      if (visible() && Date.now() - lastStart.current >= USAGE_FOCUS_GAP_MS) void refresh();
    };
    const tick = window.setInterval(() => {
      if (visible()) void refresh();
    }, USAGE_POLL_MS);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      alive.current = false;
      clearRetry(retry);
      window.clearInterval(tick);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refresh]);

  return { usage, refresh, setUsage };
}
